import React from 'react'
import styled from 'styled-components'
import { colors2 } from 'oolib'
import { TagDisplay } from 'oolib/dist/v2/components/Tags'
import { CollabFieldEngagementContext } from '@/contexts/collabFieldEngagementContext'

// The chip itself is the platform-standard TagDisplay — these two are ONLY
// positioners. Badge: straddles the field's top-right border (it used to sit
// INSIDE the field, covering the first line of text and, on small fields,
// the entire value). Inline: for tiny one-line hosts (the header title
// crumb), where an absolute badge would land on the text — flows after the
// content instead.
const StyledChipBadgePos = styled.div`
	position: absolute;
	top: -1rem;
	right: 0.8rem;
	z-index: 3;
`

const StyledChipInlinePos = styled.div`
	margin-left: 0.8rem;
	align-self: center;
	flex-shrink: 0;
`

/** No interaction inside the field for this long → the lock frees for
 * others, even though the cursor is still parked in it (see
 * isFieldStillHeld). */
const IDLE_LOCK_RELEASE_MS = 5 * 60_000;

/** Activity re-acquires at most this often — one lock:acquire per burst of
 * typing, not one per keystroke. */
const REACQUIRE_THROTTLE_MS = 10_000;

// Sits on top of the (still edit-rendered) field when someone else holds the
// lock: swallows pointer interaction without flipping the comp to its
// visually different readOnly rendering.
const StyledCollabLockOverlay = styled.div`
	position: absolute;
	inset: 0;
	z-index: 2;
	cursor: not-allowed;
	background-color: ${colors2.grey10};
`

/**
 * Focus/blur → lock acquire/release, plus the locked-by-other treatment.
 *
 * Two mechanisms decide when the lock frees, in priority order:
 *
 * 1. ENGAGEMENT (deterministic): field tooling that renders in portals
 *    (annotation lightbox, overlap picker) declares "still working on this
 *    field" through CollabFieldEngagementContext — provided here, consumed
 *    deep inside the editor plugins (portals escape the DOM but not the
 *    React tree, so the context reaches them). While engaged, blur never
 *    releases; disengaging resumes the normal release flow.
 *
 * 2. FOCUS (React-tree, not DOM): focus/blur are captured at the wrapper.
 *    Because React propagates events through the component tree, focus
 *    inside a portaled child still fires onFocusCapture here — so a
 *    blur→focus hop from the editor into its lightbox cancels the release
 *    via re-acquire. The short grace window in useCollabDoc covers the
 *    remaining blips (clicks on non-focusable regions).
 */
export const CollabFieldWrapper = ({
	valuePath,
	lockedByOther,
	lockHolderName,
	// The holder is THIS user in another tab/window — same-user locks used to
	// read "<your own name> is editing", which looks like a haunting. Callers
	// compare lock.userId against the logged-in user.
	lockHolderIsSelf,
	acquireLock,
	releaseLock,
	// Tiny one-line hosts (header title crumb) — chip flows inline after the
	// content instead of the absolute badge, which would land on the text.
	compactChip,
	// merged over the required `position: relative`, for callers whose layout
	// needs more than a plain block (e.g. a shrinkable breadcrumb crumb)
	style,
	children,
}) => {
	const engagedRef = React.useRef(false)
	const wrapperRef = React.useRef(null)
	const lastActivityRef = React.useRef(Date.now())
	const lastReacquireRef = React.useRef(0)

	// Presence + ACTIVITY check handed to acquireLock: the heartbeat (and
	// reconnect re-assert) renew the lock only while this is true.
	//
	// Presence: focus can end with NO blur event (the focused editor node is
	// removed on the collab remount), and blur-driven release alone then
	// leaks a zombie lock that heartbeats keep alive forever. Engagement
	// counts as presence: portal tooling (annotation lightbox) holds the
	// field with focus outside it.
	//
	// Activity: presence alone still holds a field FOREVER for a cursor
	// parked in it while its human is at lunch — alive tab, focused field,
	// nobody there. No keys/pointer inside the wrapper for this long → the
	// next heartbeat releases instead of renewing. Activity is judged HERE,
	// client-side, off real interaction — never off "no value change", which
	// can't tell a thinking pause (or annotation browsing) from an absent
	// user. Coming back is seamless: the first interaction re-acquires (see
	// trackActivity), so an unclaimed field resumes invisibly.
	const isFieldStillHeld = React.useCallback(
		() =>
			(engagedRef.current ||
				!!(wrapperRef.current && wrapperRef.current.contains(document.activeElement))) &&
			Date.now() - lastActivityRef.current < IDLE_LOCK_RELEASE_MS,
		[]
	)

	// Every key/pointer inside the field stamps the activity clock — and,
	// throttled, re-acquires. The re-acquire matters after an idle release:
	// the cursor never left, so no focus event will ever fire again; the
	// first returning keystroke must reclaim the lock itself (capture phase
	// runs before the input's own onChange, so that very keystroke's write
	// already passes the heartbeat gate). If someone else took the field
	// meanwhile, acquire is first-come — refused — and the overlay/bounce
	// take over via the broadcast state.
	const trackActivity = React.useCallback(() => {
		lastActivityRef.current = Date.now()
		if (lockedByOther) return
		const now = Date.now()
		if (now - lastReacquireRef.current < REACQUIRE_THROTTLE_MS) return
		lastReacquireRef.current = now
		acquireLock(valuePath, isFieldStillHeld)
	}, [lockedByOther, acquireLock, valuePath, isFieldStillHeld])

	// If the lock lands with someone ELSE while our cursor is still inside
	// (our lease lapsed — server restart / TTL — and they grabbed the field
	// before our re-grant), drop focus: the overlay already blocks pointer
	// events, but keyboard focus survives it, leaving a live cursor trapped
	// behind a "X is editing" field. The focus bounce below only covers NEW
	// focus attempts, not focus already held.
	React.useEffect(() => {
		if (!lockedByOther) return
		const active = document.activeElement
		if (active && wrapperRef.current?.contains(active)) active.blur?.()
	}, [lockedByOther])

	const engagement = React.useMemo(
		() => ({
			// Idempotent on purpose: consumers (AnnoPlugin) can fire this freely
			// on every render/mount without ref-guarding on their side — only a
			// real transition acquires/releases.
			setEngaged: (engaged) => {
				if (engaged === engagedRef.current) return
				engagedRef.current = engaged
				if (engaged) {
					lastActivityRef.current = Date.now()
					acquireLock(valuePath, isFieldStillHeld)
				} else releaseLock(valuePath) // deferred release; refocus/re-engage cancels
			},
		}),
		[valuePath, acquireLock, releaseLock, isFieldStillHeld]
	)

	return (
		<CollabFieldEngagementContext.Provider value={engagement}>
			<div
				ref={wrapperRef}
				style={{ position: 'relative', ...style }}
				onFocusCapture={(e) => {
					// tab/click focus into a locked field: bounce it (the overlay
					// swallows pointer events but not keyboard focus)
					if (lockedByOther) { e.target?.blur?.(); return }
					lastActivityRef.current = Date.now()
					acquireLock(valuePath, isFieldStillHeld)
				}}
				onBlurCapture={() => {
					if (!lockedByOther && !engagedRef.current) {
						releaseLock(valuePath)
					}
				}}
				onKeyDownCapture={trackActivity}
				onPointerDownCapture={trackActivity}
			>
				{children}
				{lockedByOther && (
					<>
						<StyledCollabLockOverlay />
						{(() => {
							const chipText = lockHolderIsSelf
								? "You're editing in another tab"
								: `${lockHolderName || 'Someone'} is editing`
							const Pos = compactChip ? StyledChipInlinePos : StyledChipBadgePos
							return (
								<Pos>
									<TagDisplay display={chipText} size="XS" />
								</Pos>
							)
						})()}
					</>
				)}
			</div>
		</CollabFieldEngagementContext.Provider>
	)
}
